// src/services/azureSitzungenService.js
// Azure Table Storage Service für Sitzungsgefässe (Jour fix, Bausitzung, ...).
// Muster: azurePendenzenService.js (projektbezogene Tabelle, PartitionKey =
// projectUuid, do/while-Paginierung, automatische Tabellenerstellung).
// US-SITZ-001: Sitzungsgefäss erfassen/bearbeiten/löschen, Anwesende, Zuordnung
// zu Pendenzen (Feld sitzungId dort).
import { getPendenzen, updatePendenz } from "./azurePendenzenService";
import { deletePraesentation } from "./azureSitzungFolienService";

const CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projectSitzungen",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const getBaseUrl = () =>
  `https://${CONFIG.storageAccount}.table.core.windows.net/${CONFIG.tableName}`;

const generateSitzungId = () =>
  `sitz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const cleanValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

// Feste Standard-Sitzungsarten (frei erweiterbar über Freitext im Formular).
export const SITZUNG_ARTEN = ["Jour fix", "Bausitzung", "Bauherrensitzung"];

let tableEnsured = false;
const ensureTableExists = async () => {
  if (tableEnsured) return true;
  try {
    const url = `https://${CONFIG.storageAccount}.table.core.windows.net/Tables?${CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify({ TableName: CONFIG.tableName }),
    });
    if (response.ok || response.status === 409) {
      tableEnsured = true;
      return true;
    }
    return false;
  } catch (error) {
    console.error("Sitzungen: Tabelle konnte nicht angelegt werden:", error);
    return false;
  }
};

const mapEntity = (item) => ({
  id: item.RowKey,
  projectUuid: item.PartitionKey,
  art: item.Art || "",
  nr: item.Nr || "",
  datum: item.Datum || "",
  // Uhrzeit von/bis (HH:MM); optional, für den Sitzungsmodus.
  vonZeit: item.VonZeit || "",
  bisZeit: item.BisZeit || "",
  ort: item.Ort || "",
  anwesende: (() => {
    try {
      return item.Anwesende ? JSON.parse(item.Anwesende) : [];
    } catch {
      return [];
    }
  })(),
  erstelltAm: item.ErstelltAm || "",
  erstelltVon: item.ErstelltVon || "",
});

const toEntity = (projectUuid, rowKey, data) => ({
  PartitionKey: projectUuid,
  RowKey: rowKey,
  Art: cleanValue(data.art),
  Nr: cleanValue(data.nr),
  Datum: cleanValue(data.datum),
  VonZeit: cleanValue(data.vonZeit),
  BisZeit: cleanValue(data.bisZeit),
  Ort: cleanValue(data.ort),
  Anwesende: JSON.stringify(Array.isArray(data.anwesende) ? data.anwesende : []),
  ErstelltAm: cleanValue(data.erstelltAm),
  ErstelltVon: cleanValue(data.erstelltVon),
});

// Alle Sitzungen eines Projekts laden (vollständig paginiert), neueste zuerst.
export const getSitzungen = async (projectUuid) => {
  if (!projectUuid) return [];
  try {
    await ensureTableExists();
    const filter = `&$filter=PartitionKey eq '${projectUuid}'`;
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;
    do {
      let url = `${getBaseUrl()}()?${CONFIG.sasToken}${filter}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }
      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }
      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);
      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allEntries
      .map(mapEntity)
      .sort((a, b) => (b.datum || "").localeCompare(a.datum || ""));
  } catch (error) {
    console.error("Fehler beim Laden der Sitzungen:", error);
    throw new Error("Die Sitzungen konnten nicht geladen werden.");
  }
};

// Neue Sitzung anlegen.
export const createSitzung = async (projectUuid, sitzungData, erstelltVon) => {
  try {
    if (!sitzungData.art || !sitzungData.art.trim()) {
      throw new Error("Bitte eine Art erfassen.");
    }
    if (!sitzungData.datum) {
      throw new Error("Bitte ein Datum erfassen.");
    }
    await ensureTableExists();
    const rowKey = generateSitzungId();
    const now = new Date().toISOString();
    const entity = toEntity(projectUuid, rowKey, {
      ...sitzungData,
      erstelltAm: now,
      erstelltVon,
    });
    const response = await fetch(`${getBaseUrl()}?${CONFIG.sasToken}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Save Error Details:", errorText);
      throw new Error(`Azure Save Error: ${response.status}`);
    }
    return mapEntity(entity);
  } catch (error) {
    console.error("Fehler beim Anlegen der Sitzung:", error);
    throw new Error(error.message || "Die Sitzung konnte nicht gespeichert werden.");
  }
};

// Sitzung aktualisieren.
export const updateSitzung = async (projectUuid, sitzungId, sitzungData) => {
  try {
    const entity = toEntity(projectUuid, sitzungId, sitzungData);
    const url = `${getBaseUrl()}(PartitionKey='${projectUuid}',RowKey='${sitzungId}')?${CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Update Error Details:", errorText);
      throw new Error(`Azure Update Error: ${response.status}`);
    }
    return mapEntity(entity);
  } catch (error) {
    console.error("Fehler beim Aktualisieren der Sitzung:", error);
    throw new Error("Die Sitzung konnte nicht aktualisiert werden.");
  }
};

// Sitzung löschen. Pendenzen bleiben erhalten, ihr sitzungId-Feld wird geleert.
// Eine eventuell hochgeladene Präsentation (US-SITZ-003, ADR 0059) wird
// mitgelöscht (Blobs + Folienzeilen) – sonst bliebe sie als Leiche zurück,
// weil ihr PartitionKey (sitzungId) auf keine Sitzung mehr verweist.
export const deleteSitzung = async (projectUuid, sitzungId) => {
  try {
    const pendenzen = await getPendenzen(projectUuid);
    const betroffene = pendenzen.filter((p) => p.sitzungId === sitzungId);
    for (const p of betroffene) {
      await updatePendenz(projectUuid, p.id, { ...p, sitzungId: "" });
    }
    await deletePraesentation(projectUuid, sitzungId);

    const url = `${getBaseUrl()}(PartitionKey='${projectUuid}',RowKey='${sitzungId}')?${CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Azure Delete Error: ${response.status}`);
    }
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen der Sitzung:", error);
    throw new Error("Die Sitzung konnte nicht gelöscht werden.");
  }
};

// Nächste sinnvolle Nummer für eine Art vorschlagen (höchste vorhandene + 1),
// nur als Vorbelegung – frei editierbar.
export const suggestNextNr = (sitzungen, art) => {
  const relevant = sitzungen.filter((s) => s.art === art);
  const max = relevant.reduce((m, s) => {
    const n = parseInt(s.nr, 10);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
  return String(max + 1);
};
